import React, { FC, useState, useCallback } from "react";
import { Box, Paper, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CONTENT_TYPE_CONFIG } from "../../../config/contentTypeConfig";
import { formatContentTypeName } from "../helper";

interface ContentType {
    id: number;
    name: string;
    [key: string]: any;
}

interface ContentTypeCardProps {
    contentType: ContentType;
    selected: boolean;
    onClick: () => void;
}

function fallbackGradient(): [string, string] {
    return ["#F5E8CE", "#C49840"];
}

export const ContentTypeCard: FC<ContentTypeCardProps> = ({ contentType, selected, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const [videoReady, setVideoReady] = useState(false);
    const [videoFailed, setVideoFailed] = useState(false);

    const config = CONTENT_TYPE_CONFIG[contentType.name] ?? null;
    const displayTitle = config?.title ?? formatContentTypeName(contentType.name);
    const [c1, c2] = fallbackGradient();

    const handleMouseEnter = useCallback(() => setHovered(true), []);

    const handleMouseLeave = useCallback(() => {
        setHovered(false);
        setVideoReady(false);
    }, []);

    const showVideo = !!config?.videoUrl && !videoFailed;
    const showPoster = !!config?.posterUrl && (!showVideo || !videoReady);
    const showGradient = !config;
    const showFallbackGradient = !!config && !showVideo && !config.posterUrl;

    return (
        <Paper
            elevation={0}
            onClick={onClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
                borderRadius: "14px",
                border: selected || hovered ? "2px solid var(--accent)" : "1px solid var(--border)",
                background: selected ? "var(--tint)" : "var(--surface)",
                overflow: "hidden",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                transition: "border .1s ease, background .1s ease",
                userSelect: "none",
            }}
        >
            {/* Thumbnail / video area */}
            <Box
                sx={{
                    position: "relative",
                    height: { xs: 120, sm: 104 },
                    overflow: "hidden",
                    background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`,
                    flexShrink: 0,
                }}
            >
                {showPoster && (
                    <img
                        src={config!.posterUrl}
                        alt={displayTitle}
                        style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                    />
                )}
                {showVideo && hovered && (
                    <video
                        key={contentType.name}
                        poster={config!.posterUrl}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        onPlaying={() => setVideoReady(true)}
                        onError={() => setVideoFailed(true)}
                        style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                    >
                        <source src={config!.videoUrl} type="video/webm" />
                    </video>
                )}
                {(showGradient || showFallbackGradient) && (
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            height: "100%",
                            background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`,
                            overflow: "hidden",
                        }}
                    >
                        {/* Diagonal hatch */}
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                opacity: 0.35,
                                backgroundImage:
                                    "repeating-linear-gradient(45deg, transparent 0 10px, rgba(255,255,255,0.18) 10px 11px)",
                            }}
                        />
                        {/* Centered name */}
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 1,
                                textAlign: "center",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: displayTitle.length > 10 ? 18 : 22,
                                    fontWeight: 700,
                                    color: "#8a6e3f",
                                    letterSpacing: -0.5,
                                    lineHeight: 1.05,
                                    fontFamily: "CircularStd",
                                }}
                            >
                                {displayTitle}
                            </Typography>
                        </Box>
                        {/* NEW badge — only for types with no config entry */}
                        {showGradient && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    left: 8,
                                    background: "var(--accent)",
                                    color: "#fff",
                                    fontSize: 9.5,
                                    fontWeight: 700,
                                    letterSpacing: 0.6,
                                    px: "7px",
                                    py: "3px",
                                    borderRadius: "99px",
                                    textTransform: "uppercase",
                                    fontFamily: "CircularStd",
                                }}
                            >
                                New
                            </Box>
                        )}
                        {/* Example soon — only for types with no config entry */}
                        {showGradient && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 8,
                                    right: 8,
                                    fontSize: 9.5,
                                    fontWeight: 600,
                                    color: "#8a6e3f",
                                    opacity: 0.8,
                                    letterSpacing: 0.4,
                                    textTransform: "uppercase",
                                    background: "rgba(255,255,255,0.5)",
                                    px: "6px",
                                    py: "2px",
                                    borderRadius: "4px",
                                    fontFamily: "CircularStd",
                                }}
                            >
                                Example soon
                            </Box>
                        )}
                    </Box>
                )}

                {/* Selected checkmark badge */}
                {selected && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 2,
                            color: "var(--accent)",
                            background: "var(--surface)",
                            borderRadius: "50%",
                            width: 22,
                            height: 22,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                        }}
                    >
                        <CheckCircleIcon sx={{ fontSize: 22, color: "var(--accent)" }} />
                    </Box>
                )}
            </Box>

            {/* Card body */}
            <Box sx={{ padding: "7px 11px 9px", flexShrink: 0 }}>
                <Box display="flex" alignItems="baseline" justifyContent="space-between" gap="6px">
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "var(--text)",
                            letterSpacing: -0.1,
                            fontFamily: "CircularStd",
                        }}
                    >
                        {displayTitle}
                    </Typography>
                </Box>
                <Typography
                    sx={{
                        fontSize: 11.5,
                        color: "var(--text-2)",
                        mt: "2px",
                        lineHeight: 1.35,
                        fontFamily: "CircularStd",
                    }}
                >
                    {config?.description ?? "Custom format — scope with your producer."}
                </Typography>
            </Box>
        </Paper>
    );
};
